import Tooltip from '@mui/material/Tooltip';
import makeStyles from '@mui/styles/makeStyles';
import NRColoredBarStyles from './NRColoredBar.styles';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { capitalizeFirstLetter, round } from '../../utils/utils';
import { capitalizeFirstWord } from '../../utils/utils';
import NRSecondaryLink from '../NRSecondaryLink/NRSecondaryLink';
import ColoredCircle from '../../NRIcons/ColoredCircle';
import { useTheme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

const useStyles = makeStyles(NRColoredBarStyles);

const ColoredBarTooltipContent = ({ title, color, value, showPercentage = false, total, capitalizeFirstOnly, capitalizeEachWord }) => {
  const classes = useStyles();
  return (
    <>
      {!!title && !!color && !!value ? (
        <div className={classes.tooltipContainer}>
          <ColoredCircle color={color} className={classes.marginRight} />
          <div className={classes.marginRight}>
            {`${capitalizeEachWord ? capitalizeFirstWord(title) : capitalizeFirstOnly ? capitalizeFirstLetter(title) : title}: ${value}`}{' '}
            {`${showPercentage ? '(' + round((value / total) * 100, 1) + '%)' : ''}`}
          </div>
        </div>
      ) : (
        ''
      )}
    </>
  );
};

const NRColoredBar = ({
  innerKey,
  config,
  emptyBarColor,
  showTotal = true,
  showValues = true,
  handleOnTotalClick,
  showPercentage = false,
  height = 24,
  capitalizeFirstOnly = true,
  capitalizeEachWord = true,
  isLoading,
  summaryLabel,
  disableOnClick = false
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const total = Array.isArray(config) && config.reduce((a, b) => a + b?.value, 0);
  const EMPTY_BAR_COLOR = emptyBarColor || theme.palette.noData.background;

  const a11ySummary = total
    ? `${summaryLabel || 'Results'} summary: ${config
        .map(item => {
          const percent = total ? round((item.value / total) * 100, 1) : 0;
          const label = item.title;
          return `${label} ${item.value} (${percent}%)`;
        })
        .join(', ')}. Total ${total}.`
    : `${summaryLabel || 'Results'} summary: No data available.`;

  return (
    <div className={classes.coloredBar} style={{ height }} role="group" aria-label={`Result distribution bar${total ? `, total ${total}` : ''}`}>
      <Typography variant="srOnly" id={`${innerKey}-summary`}>
        {a11ySummary}
      </Typography>
      <div className={classes.segmentsContainer}>
        {!!total && !isLoading ? (
          config.map(configItem => {
            const percent = total ? round((configItem.value / total) * 100, 1) : 0;
            const segmentLabel = `${configItem.title}: ${percent}% (${configItem.value})`;

            return (
              <NRColoredSegment
                key={`${innerKey}-${configItem.title}-${configItem.value}`}
                width={(configItem.value * 100) / total}
                color={configItem.color}
                value={configItem.value}
                tooltipTitle={
                  !!configItem?.toolTip ? (
                    configItem.toolTip
                  ) : (
                    <ColoredBarTooltipContent
                      title={configItem?.title}
                      color={configItem.color}
                      value={configItem.value}
                      showPercentage={showPercentage}
                      total={total}
                      capitalizeFirstOnly={capitalizeFirstOnly}
                      capitalizeEachWord={capitalizeEachWord}
                    />
                  )
                }
                showValues={showValues}
                handleOnClick={!disableOnClick ? configItem?.action : undefined}
                ariaLabel={segmentLabel}
              />
            );
          })
        ) : (
          <NRColoredSegment width={100} color={EMPTY_BAR_COLOR} showValues={false} ariaLabel="No data available" disableOnClick={disableOnClick} />
        )}
      </div>
      {showTotal && (
        <Box
          sx={{
            marginLeft: theme.spacing(1),
            minWidth: theme.spacing(3),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: !disableOnClick ? 'pointer' : 'default'
          }}
        >
          {isLoading ? (
            <CircularProgress size={20} />
          ) : (
            <NRSecondaryLink
              value={total}
              disabledClick={disableOnClick}
              handleOnClick={!disableOnClick ? () => handleOnTotalClick() : undefined}
              aria-label={`Total value: ${total}`}
            />
          )}
        </Box>
      )}
    </div>
  );
};

const NRColoredSegment = ({
  width,
  color,
  value,
  tooltipTitle,
  showValues,
  valueDisplayWithThreshold = 24,
  handleOnClick,
  ariaLabel,
  disableOnClick = false
}) => {
  const mySegment = React.createRef();
  const [segmentWidth, setSegmentWidth] = useState();

  useEffect(() => {
    !!mySegment?.current?.offsetWidth && setSegmentWidth(mySegment?.current?.offsetWidth);
  }, [mySegment?.current?.offsetWidth]);

  const classes = useStyles();
  return value !== 0 ? (
    <Tooltip title={tooltipTitle || ''} placement={'top'}>
      <div
        ref={mySegment}
        onClick={!disableOnClick ? () => handleOnClick && handleOnClick() : undefined}
        style={{ width: `${width}%`, backgroundColor: color }}
        className={clsx(classes.valueContainer, classes.segment, { [classes.cursorDefault]: !handleOnClick || disableOnClick })}
        role="img"
        aria-label={ariaLabel}
      >
        {showValues && segmentWidth > valueDisplayWithThreshold && value}
      </div>
    </Tooltip>
  ) : (
    <></>
  );
};

export default NRColoredBar;
